<template>
  <div class="user-register-page">
    <div class="register-container">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <a-spin size="large" />
        <p class="mt-16">Validating registration link...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <a-result
          status="error"
          title="Invalid Registration Link"
          :sub-title="error"
        >
          <template #extra>
            <a-button type="primary" @click="$router.push('/login')">
              Go to Login
            </a-button>
          </template>
        </a-result>
      </div>

      <!-- Registration Form -->
      <div v-else class="register-form">
        <div class="form-header">
          <h1>User Registration</h1>
          <p>Complete your registration to create your account</p>
        </div>

        <a-form
          :form="form"
          @submit="handleSubmit"
          class="registration-form"
          layout="vertical"
        >
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="First Name" required>
                <a-input
                  v-decorator="[
                    'firstName',
                    {
                      rules: [
                        { required: true, message: 'Please enter your first name' },
                        { min: 2, message: 'First name must be at least 2 characters' }
                      ]
                    }
                  ]"
                  placeholder="Enter your first name"
                  size="large"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Last Name" required>
                <a-input
                  v-decorator="[
                    'lastName',
                    {
                      rules: [
                        { required: true, message: 'Please enter your last name' },
                        { min: 2, message: 'Last name must be at least 2 characters' }
                      ]
                    }
                  ]"
                  placeholder="Enter your last name"
                  size="large"
                />
              </a-form-item>
            </a-col>
          </a-row>

          <a-form-item label="Email Address" required>
            <a-input
              v-decorator="[
                'email',
                {
                  rules: [
                    { required: true, message: 'Please enter your email address' },
                    { type: 'email', message: 'Please enter a valid email address' }
                  ]
                }
              ]"
              placeholder="Enter your email address"
              size="large"
            />
          </a-form-item>

          <a-form-item label="Phone Number" required>
            <a-input
              v-decorator="[
                'phoneNumber',
                {
                  rules: [
                    { required: true, message: 'Please enter your phone number' },
                    { pattern: /^[\+]?[1-9][\d\-]{0,15}$/, message: 'Please enter a valid phone number' }
                  ]
                }
              ]"
              placeholder="Enter your phone number"
              size="large"
            />
          </a-form-item>


          <a-form-item label="Password" required>
            <a-input-password
              v-decorator="[
                'password',
                {
                  rules: [
                    { required: true, message: 'Please enter a password' }
                  ]
                }
              ]"
              placeholder="Enter your password"
              size="large"
            />
          </a-form-item>

          <a-form-item label="Confirm Password" required>
            <a-input-password
              v-decorator="[
                'confirmPassword',
                {
                  rules: [
                    { required: true, message: 'Please confirm your password' },
                    {
                      validator: this.compareToFirstPassword,
                    }
                  ]
                }
              ]"
              placeholder="Confirm your password"
              size="large"
            />
          </a-form-item>

          <a-form-item>
            <a-button
              type="primary"
              html-type="submit"
              size="large"
              :loading="submitting"
              block
            >
              Create Account
            </a-button>
          </a-form-item>
        </a-form>

        <div class="form-footer">
          <p>Already have an account? <a @click="window.location.href='https://tender.gurmanlogistics.com/login'">Sign in</a></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import userAPI from '../../api/user';

export default {
  name: 'UserRegister',
  data() {
    return {
      loading: true,
      error: '',
      submitting: false,
      token: '',
      companyId: '',
    };
  },
  beforeCreate() {
    this.form = this.$form.createForm(this);
  },
  async mounted() {
    // Get token and company ID from URL query parameters
    this.token = this.$route.query.token;
    this.companyId = this.$route.query.company_id;
    
    if (!this.token) {
      this.error = 'No registration token provided';
      this.loading = false;
      return;
    }

    if (!this.companyId) {
      this.error = 'No company ID provided';
      this.loading = false;
      return;
    }

    // Validate the token
    try {
      await userAPI.validateRegisterToken(this.token);
      this.loading = false;
    } catch (err) {
      this.error = err.response?.data?.error || 'Invalid or expired registration token';
      this.loading = false;
    }
  },
  methods: {
    compareToFirstPassword(rule, value, callback) {
      const form = this.form;
      if (value && value !== form.getFieldValue('password')) {
        callback('The two passwords that you entered do not match!');
      } else {
        callback();
      }
    },

    async handleSubmit(e) {
      e.preventDefault();
      
      this.form.validateFields(async (err, values) => {
        if (err) return;

        this.submitting = true;
        
        try {
          const userData = {
            first_name: values.firstName,
            last_name: values.lastName,
            email: values.email,
            phone_number: values.phoneNumber,
            password: values.password,
            register_token: this.token,
            company_id: this.companyId,
          };

          await userAPI.registerWithToken(userData);
          
          this.$message.success('Account created successfully! Please sign in.');
          window.location.href = 'https://tender.gurmanlogistics.com/login';
        } catch (err) {
          this.$message.error(err.response?.data?.error || 'Failed to create account');
        } finally {
          this.submitting = false;
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.user-register-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  .register-container {
    background: white;
    border-radius: 12px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 600px;
    padding: 40px;

    .loading-state,
    .error-state {
      text-align: center;
      padding: 40px 20px;
    }

    .register-form {
      .form-header {
        text-align: center;
        margin-bottom: 32px;

        h1 {
          font-size: 28px;
          font-weight: 600;
          color: #333;
          margin-bottom: 8px;
        }

        p {
          color: #666;
          font-size: 16px;
          margin: 0;
        }
      }

      .registration-form {
        .ant-form-item {
          margin-bottom: 20px;
        }
      }

      .form-footer {
        text-align: center;
        margin-top: 24px;
        padding-top: 24px;
        border-top: 1px solid #f0f0f0;

        p {
          margin: 0;
          color: #666;

          a {
            color: #1890ff;
            cursor: pointer;
            text-decoration: none;

            &:hover {
              text-decoration: underline;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .user-register-page {
    .register-container {
      padding: 20px;
      margin: 10px;

      .register-form {
        .form-header {
          h1 {
            font-size: 24px;
          }
        }
      }
    }
  }
}
</style>
